import React, { useState, useMemo } from 'react';
import { useAICoach } from './AICoachContext';
import { Button, ChatMessageView, Icon } from '@aidkitorg/component-library';
import { useMarkdown } from '../Util';
import { ChatEvent } from './ChatChannel';

export function AICoachButton() {
  const [chatIsOpen, setChatIsOpen] = useState(false);

  if (!chatIsOpen) {
    return <div className="fixed bottom-10 right-10 sm:bottom-12 sm:right-12 md:bottom-14 md:right-14 z-50">
      <button
        onClick={() => setChatIsOpen(true)}
        className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full py-2.5 px-4 sm:py-3 sm:px-5 flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        style={{
          paddingTop: "0.5rem",
          paddingBottom: "0.5rem",
          paddingLeft: "0.75rem",
          paddingRight: "0.75rem",
          fontSize: "clamp(0.875rem, 2vw, 1.25rem)"
        }}
        aria-label="Ask Opal: Your AI Coach"
      >
        <Icon name="ChatBubbleLeftRightIcon" role="decorative" className="text-white" size="lg" />
        <span className="text-xl sm:text-md md:text-xl ml-2" style={{ marginLeft: "0.5rem" }}>Ask Opal</span>
      </button>
    </div>
  }

  return <AICoachChatWindow onClickMinimize={() => setChatIsOpen(false)} />
}

export function AICoachChatWindow(props: { onClickMinimize: () => void }) {
  const [inputText, setInputText] = useState<string>("");
  const {
    transcript,
    hasAcceptedTerms,
    setHasAcceptedTerms,
    enabled,
    active,
    sendMessage
  } = useAICoach();

  const handleChatSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (inputText) {
      sendMessage(inputText)
    }
    setInputText('');
  }

  function MessageContent({ message }: { message: ChatEvent }) {

    const content = message.content?.map(c => c.text ?? c.transcript).join('');

    return useMarkdown(content);
  }

  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    items = items.concat(transcript.filter(t => true || !t.meta?.hidden).map(m => ({
      role: m.role,
      meta: m.meta,
      content: <MessageContent message={m} />
    })));

    return items;
  }, [transcript]);

  /* Sliding chat panel - full screen on mobile, partial on desktop */
  return <div className="fixed bottom-0 right-0 z-50 w-full sm:w-80 md:w-[360px] lg:w-[450px] max-w-[100vw] sm:max-w-[90vw] 
                      h-full sm:h-[90vh] md:h-[75vh] bg-white shadow-lg flex flex-col sm:rounded-t-lg">
    {/* Header */}
    <div className="border-b border-gray-200">
      <div className="flex flex-col p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://i.postimg.cc/GmczHr3p/avatar-opal.png" alt="Opal" className="w-12 h-12 rounded-full mr-3" />
            <h3 className="text-2xl font-semibold mt-2">Chat with Opal</h3>
          </div>
          {/* Minimize button */}
          <div className="group relative">
            <button
              className="text-gray-600 bg-gray-200 hover:bg-gray-300 hover:text-indigo-600 p-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
              onClick={props.onClickMinimize}
              aria-label="Minimize Chat"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.5 3.5L9 9M9 9V4M9 9H4M20.5 20.5L15 15M15 15V20M15 15H20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Minimize Chat
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Chat content */}
    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src="https://i.postimg.cc/GmczHr3p/avatar-opal.png" alt="Opal" className="rounded-full" />
        },
        user: {
          side: 'right',
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />

    {/* Terms of Service - shown only before acceptance */}
    {!hasAcceptedTerms && (
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <h4 className="font-semibold text-gray-800 mb-2">Terms of Service</h4>
        <p className="text-sm text-gray-600 mb-3">
          By using Opal, you agree to our terms of service and privacy policy.
          Your conversations may be recorded and analyzed to improve our service.
        </p>
        <button
          onClick={() => setHasAcceptedTerms(true)}
          className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors"
        >
          Accept Terms & Continue
        </button>
      </div>
    )}

    {/* Message input - only shown after terms acceptance */}
    {hasAcceptedTerms && (
      <div className="border-t border-gray-200 p-3">
        <div className="flex items-center gap-2">
          {/* Microphone button */}
          <Button
            variant="secondary"
            shape="circle"
            aria-label="Voice input"
          >
            <Icon name="MicrophoneIcon" />
          </Button>

          <form
            className="flex flex-1 gap-2"
            onSubmit={handleChatSubmit}>
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Type a message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <Button
              type="submit"
              isDisabled={!inputText || !active}
            >
              <Icon
                className="-rotate-45 -mt-1 -mr-1"
                name="PaperAirplaneIcon" />
            </Button>

          </form>
        </div>
      </div>
    )}
  </div>
}
